import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, Phone, Instagram, Linkedin, Facebook, ChevronDown, ArrowUpRight, CalendarCheck } from "lucide-react";
import { COMPANY, COMPANIES, SERVICES, NAV_SERVICES } from "../../data";
import { CITIES } from "../../content/cities";
import { trackPageView } from "../../lib/analytics";
import { asset } from "../../lib/images";
import { useNearViewport } from "../../lib/useNearViewport";
import { LenderLink } from "./AcornLink";
import { ResponsiveImage } from "./ResponsiveImage";

const LOGO_ALT = "ID Builder Construction";
const STATE_NAMES = { WA: "Washington", ID: "Idaho", MT: "Montana", AZ: "Arizona" };
const serviceName = (s) => s.title.split("—")[0].trim();
const cityLabel = (c) => (c.hq ? `${c.name} (HQ)` : c.label || c.name);
const cityLink = (c) => ({ label: cityLabel(c), to: `/service-areas/${c.slug}`, testid: `nav-city-${c.slug}` });
// Locations grouped by state — built from the city pages that exist. Per state: [name, office cities, cities served from HQ];
// offices come first and the served list sits under an "Also serving" sub-label.
export const LOCATION_GROUPS = ["WA", "ID", "MT", "AZ"].map((st) => {
  const cities = CITIES.filter((c) => c.state === st);
  return [STATE_NAMES[st], cities.filter((c) => !c.servedFrom).map(cityLink), cities.filter((c) => c.servedFrom).map(cityLink)];
}).filter(([, offices, served]) => offices.length + served.length);
export const ALSO_SERVING = "Also serving";
// Mobile accordion sections: [heading, links][] — the served cities get their own "Also serving" heading.
const LOCATION_SECTIONS = LOCATION_GROUPS.flatMap(([state, offices, served]) => [[state, offices], ...(served.length ? [[ALSO_SERVING, served]] : [])]);
const ALL_LOCATIONS = { label: "All locations", to: "/service-areas", testid: "nav-all-locations", accent: true };

const PRICING = [
  { label: "Estimate calculator", to: "/estimate", desc: "Price your project in 90 seconds", testid: "nav-pricing-estimate" },
  { label: "Cost guides", to: "/cost", desc: "What projects cost in 2026", testid: "nav-pricing-cost" },
  { label: "Financing", to: "/financing", desc: "Pre-qualify with no credit impact", testid: "nav-pricing-financing" },
  { label: "3D Design Studio", to: "/design", badge: "Beta", testid: "nav-pricing-design" },
];
const ABOUT_COLS = [
  ["Company", [
    { label: "Our Story", to: "/about", testid: "nav-about-story" },
    { label: "Our Process", to: "/process", testid: "nav-about-process" },
    { label: "What Makes Us Different", to: "/about#what-makes-us-different", testid: "nav-about-different" },
    { label: "Licenses & Certifications", to: "/about#credentials", testid: "nav-about-credentials" },
    { label: "Careers", to: "/careers", testid: "nav-about-careers" },
    { label: "Trade Partners", to: "/trade-partners", testid: "nav-about-trade-partners" },
  ]],
  ["Proof & resources", [
    { label: "Reviews", to: "/reviews", testid: "nav-about-reviews" },
    { label: "Resource Center", to: "/blog", testid: "nav-about-blog" },
    { label: "FAQ", to: "/faq", testid: "nav-about-faq" },
  ]],
];

// Six top-level items. `match` lists the URL prefixes that count as "inside" the section (Pricing lights up on /cost…).
const NAV = [
  { label: "Services", to: "/services", kind: "services", match: ["/services"] },
  { label: "Projects", to: "/projects", match: ["/projects"] },
  { label: "Pricing", to: "/estimate", kind: "pricing", match: ["/estimate", "/cost", "/financing"] },
  { label: "Design Studio", to: "/design", badge: "Beta", match: ["/design", "/my-designs"] },
  { label: "Locations", to: "/service-areas", kind: "locations", match: ["/service-areas"] },
  { label: "About", to: "/about", kind: "about", match: ["/about", "/process", "/careers", "/trade-partners", "/reviews", "/blog", "/faq"] },
];
const inSection = (pathname, item) => item.match.some((p) => pathname === p || pathname.startsWith(`${p}/`));

// Sticky call bar: phones only, every page except /contact, /reviews and the /review short link.
const NO_CALLBAR = ["/contact", "/reviews", "/review"];
export const showCallBar = (pathname) => !NO_CALLBAR.some((p) => pathname === p || pathname.startsWith(`${p}/`));

// Desktop hover menus. The panel is always in the DOM (CSS hides it) so every dropdown link is real HTML for crawlers.
// Opens after a 150 ms hover delay or at once on keyboard focus; closes 180 ms after leaving BOTH the trigger and the
// panel (no dead gap), on Esc, and on navigation.
const HoverMenu = ({ children, className = "", testId }) => {
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const { pathname } = useLocation();
  const clear = () => clearTimeout(timer.current);
  const openIn = (ms) => { clear(); timer.current = setTimeout(() => setOpen(true), ms); };
  const close = () => { clear(); timer.current = setTimeout(() => setOpen(false), 180); };
  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => { clearTimeout(timer.current); setOpen(false); }, [pathname]);
  // Esc closes an open panel even when it was opened by hover (the key then lands on <body>, not inside the menu).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") { clearTimeout(timer.current); setOpen(false); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  return (
    <div className={`${className} h-20 flex items-center ${open ? "menu-open" : ""}`} onMouseEnter={() => openIn(150)} onMouseLeave={close} onFocus={() => openIn(0)} onBlur={close} data-open={open} data-testid={testId}>
      {children(open)}
    </div>
  );
};

const Badge = ({ children, testId }) => <span className="text-[9px] leading-none font-bold uppercase tracking-wider text-[#FF9000] border border-[#FF9000]/50 rounded-full px-1.5 py-0.5" data-testid={testId}>{children}</span>;

const DropLink = ({ d }) => (
  <Link to={d.to} className={`block px-4 py-2.5 rounded-lg hover:bg-white/5 transition-colors group/link ${d.accent ? "text-[#FF9000]" : ""}`} data-testid={d.testid}>
    <span className={`text-sm font-semibold transition-colors inline-flex items-center gap-1.5 ${d.accent ? "" : "text-white/90 group-hover/link:text-[#FF9000]"}`}>{d.label}{d.badge && <Badge>{d.badge}</Badge>}</span>
    {d.desc && <span className="block text-white/50 text-xs mt-0.5">{d.desc}</span>}
  </Link>
);

const PanelLabel = ({ children }) => <div className="text-[10px] uppercase tracking-widest text-[#FF9000]/80 font-bold px-4 pt-2 pb-1">{children}</div>;

// Services: the mega-menu, unchanged apart from its card title no longer being a heading (it preceded every page's H1).
const ServicesPanel = () => (
  <div data-testid="services-mega" className="menu-panel fixed left-0 right-0 top-20 z-40">
    <div className="mx-auto max-w-[1400px] px-5 lg:px-10 pt-3">
      <div className="bg-[#161616] backdrop-blur-xl border border-white/10 rounded-2xl p-6 grid grid-cols-[0.9fr_2.1fr] gap-6 shadow-2xl">
        <Link to="/services" className="group/card relative rounded-xl overflow-hidden min-h-[210px]">
          <ResponsiveImage src={SERVICES[0].img} sizes="420px" alt="" className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover/card:opacity-55 transition-opacity" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
          <div className="relative p-5 h-full flex flex-col justify-end">
            <span className="text-[10px] uppercase tracking-[0.25em] text-[#FF9000] font-bold">All under one roof</span>
            <p className="font-display text-2xl text-white uppercase mt-1 leading-tight">{NAV_SERVICES.length} services,<br />one crew</p>
            <span className="mt-3 inline-flex items-center gap-1 text-white text-sm font-bold">View all services <ArrowUpRight size={15} className="text-[#FF9000]" /></span>
          </div>
        </Link>
        <div className="grid grid-cols-3 gap-x-5 gap-y-0.5">
          {NAV_SERVICES.map((s) => (
            <Link key={s.slug} to={`/services/${s.slug}`} className="group/item py-2 px-2 rounded-lg hover:bg-white/5 transition-colors" data-testid={`mega-${s.slug}`}>
              <div className="text-[10px] uppercase tracking-widest text-[#FF9000]/80 font-bold">{s.cat}</div>
              <div className="text-white/90 text-sm font-semibold group-hover/item:text-[#FF9000] transition-colors leading-tight">{serviceName(s)}</div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const PricingPanel = () => (
  <div data-testid="pricing-dropdown" className="menu-panel absolute left-0 top-full -mt-2 pt-2 z-50">
    <div className="bg-[#161616] backdrop-blur-xl border border-white/10 rounded-2xl p-3 w-72 shadow-2xl">{PRICING.map((d) => <DropLink key={d.to} d={d} />)}</div>
  </div>
);

const LocationsPanel = () => (
  <div data-testid="locations-dropdown" className="menu-panel absolute left-0 top-full -mt-2 pt-2 z-50">
    <div className="bg-[#161616] backdrop-blur-xl border border-white/10 rounded-2xl p-3 w-[620px] shadow-2xl">
      <div className="grid grid-cols-4 gap-2">
        {LOCATION_GROUPS.map(([state, offices, served]) => (
          <div key={state} data-testid={`nav-locations-${state.toLowerCase()}`}>
            <PanelLabel>{state}</PanelLabel>
            {offices.map((d) => <DropLink key={d.to} d={d} />)}
            {served.length > 0 && (<>
              <div className="mt-1 pt-2 border-t border-white/10" data-testid={`nav-locations-${state.toLowerCase()}-also-serving`}><PanelLabel>{ALSO_SERVING}</PanelLabel></div>
              {served.map((d) => <DropLink key={d.to} d={d} />)}
            </>)}
          </div>
        ))}
      </div>
      <div className="border-t border-white/10 mt-2 pt-2"><DropLink d={ALL_LOCATIONS} /></div>
    </div>
  </div>
);

const AboutPanel = () => (
  <div data-testid="about-dropdown" className="menu-panel absolute right-0 top-full -mt-2 pt-2 z-50">
    <div className="bg-[#161616] backdrop-blur-xl border border-white/10 rounded-2xl p-3 w-[520px] shadow-2xl grid grid-cols-2 gap-2">
      {ABOUT_COLS.map(([label, links]) => (
        <div key={label} data-testid={`nav-about-${label.split(" ")[0].toLowerCase()}`}>
          <PanelLabel>{label}</PanelLabel>
          {links.map((d) => <DropLink key={d.to} d={d} />)}
        </div>
      ))}
    </div>
  </div>
);

const PANELS = { services: ServicesPanel, pricing: PricingPanel, locations: LocationsPanel, about: AboutPanel };

export const ScrollTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = hash.slice(1);
      const t = setTimeout(() => {
        const el = document.getElementById(id);
        if (!el) return;
        if (window.__lenis) window.__lenis.scrollTo(el, { offset: -90 });
        else el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
      return () => clearTimeout(t);
    }
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
    const t = setTimeout(() => trackPageView(pathname), 60);
    return () => clearTimeout(t);
  }, [pathname, hash]);
  return null;
};

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [solid, setSolid] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => {
    const s = () => setSolid(window.scrollY > 40);
    window.addEventListener("scroll", s); return () => window.removeEventListener("scroll", s);
  }, []);
  const linkClass = (active) => `link-underline whitespace-nowrap text-[12px] font-semibold uppercase tracking-wider transition-colors flex items-center gap-1 ${active ? "text-[#FF9000]" : "text-white/85 hover:text-white"}`;
  return (
    <>
      <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${solid ? "bg-[#111]/95 backdrop-blur-md border-b border-white/10" : "bg-gradient-to-b from-black/70 to-transparent"}`} data-testid="site-header">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 h-20 flex items-center justify-between">
          <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
            <Link to="/" className="flex items-center group" data-testid="logo-link" aria-label="ID Builder Construction home">
              <img src={asset("/logo.webp")} alt={LOGO_ALT} className="h-14 lg:h-16 w-auto transition-transform duration-500 group-hover:scale-105" style={{ filter: "drop-shadow(0 2px 14px rgba(0,0,0,0.55))" }} width="160" height="76" />
            </Link>
          </motion.div>

          <nav className="hidden xl:flex items-center gap-5" aria-label="Primary">
            {NAV.map((item) => {
              const active = inSection(pathname, item);
              const testid = `nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`;
              const Panel = PANELS[item.kind];
              if (!Panel) return <Link key={item.to} to={item.to} className={linkClass(active)} aria-current={active ? "page" : undefined} data-testid={testid}>{item.label}{item.badge && <Badge>{item.badge}</Badge>}</Link>;
              return (
                <HoverMenu key={item.to} className={`group ${item.kind === "services" ? "" : "relative"}`} testId={`${testid}-menu`}>
                  {(isOpen) => (<>
                    <Link to={item.to} className={linkClass(active)} aria-current={active ? "page" : undefined} aria-expanded={isOpen} data-testid={testid}>
                      {item.label} <ChevronDown size={14} className="transition-transform duration-300 menu-chevron" />
                    </Link>
                    <Panel />
                  </>)}
                </HoverMenu>
              );
            })}
          </nav>

          <div className="flex items-center gap-3 shrink-0">
            <a href={COMPANY.phoneHref} aria-label={`Call ${COMPANY.phone}`} data-location="header" className="hidden md:flex items-center gap-2 whitespace-nowrap text-white text-sm font-bold border border-white/20 rounded-full px-4 py-2 max-lg:min-h-11 hover:border-[#FF9000] hover:text-[#FF9000] transition-colors" data-testid="header-phone"><Phone size={15} className="text-[#FF9000]" /><span className="hidden xl:inline">{COMPANY.phone}</span></a>
            <Link to="/contact" data-cta="free-visit" data-location="header" className="hidden sm:inline-flex btn-accent whitespace-nowrap rounded-full px-6 py-3 max-lg:min-h-11 text-[12px] uppercase tracking-wider items-center gap-1.5" data-testid="header-cta">Free Visit <ArrowUpRight size={15} /></Link>
            <button className="xl:hidden text-white h-11 w-11 -mr-2 grid place-items-center" onClick={() => setOpen(true)} aria-label="Open menu" data-testid="menu-open"><Menu size={26} /></button>
          </div>
        </div>
      </header>

      <MobileDrawer open={open} close={() => setOpen(false)} />
    </>
  );
};

// Mobile menu groups mirror the desktop menu: [heading | null, links][] per section.
const MOBILE_GROUPS = [
  { label: "Services", to: "/services", sections: [[null, [...NAV_SERVICES.map((s) => ({ label: serviceName(s), to: `/services/${s.slug}`, testid: `nav-m-${s.slug}` })), { label: "All services", to: "/services", testid: "nav-m-all-services", accent: true }]]] },
  { label: "Projects", to: "/projects" },
  { label: "Pricing", to: "/estimate", sections: [[null, PRICING]] },
  { label: "Design Studio", to: "/design", badge: "Beta" },
  { label: "Locations", to: "/service-areas", sections: [...LOCATION_SECTIONS, [null, [ALL_LOCATIONS]]] },
  { label: "About", to: "/about", sections: ABOUT_COLS },
];

// Full-screen drawer: 48px rows, each section expands as a real accordion; Call + Book a free visit pinned to the bottom.
const MobileDrawer = ({ open, close }) => {
  const [expanded, setExpanded] = useState(null);
  const row = "min-h-[48px] flex items-center font-display text-2xl text-white/90 hover:text-[#FF9000] uppercase";
  return (
    <div className={`fixed inset-0 z-[70] bg-[#111] transition-transform duration-500 xl:hidden flex flex-col ${open ? "translate-x-0" : "translate-x-full"}`} data-testid="mobile-menu" aria-hidden={!open} inert={!open}>
      <div className="flex items-center justify-between px-5 h-20 border-b border-white/10 shrink-0">
        <img src={asset("/logo.webp")} alt={LOGO_ALT} className="h-11 w-auto" width="93" height="44" />
        <button onClick={close} className="text-white h-11 w-11 -mr-2 grid place-items-center" aria-label="Close menu" data-testid="menu-close"><X size={26} /></button>
      </div>
      <nav className="px-6 py-3 flex flex-col flex-1 overflow-y-auto overscroll-contain" aria-label="Mobile">
        <Link to="/" onClick={close} className={row} data-testid="m-nav-home">Home</Link>
        {MOBILE_GROUPS.map((item) => {
          const key = item.label.toLowerCase().replace(/\s+/g, "-");
          if (!item.sections) return <Link key={item.to} to={item.to} onClick={close} className={`${row} gap-2`} data-testid={`m-nav-${key}`}>{item.label}{item.badge && <Badge>{item.badge}</Badge>}</Link>;
          const isOpen = expanded === key;
          return (
            <div key={item.to}>
              <div className="flex items-center">
                <Link to={item.to} onClick={close} className={`${row} flex-1`} data-testid={`m-nav-${key}`}>{item.label}</Link>
                <button type="button" onClick={() => setExpanded(isOpen ? null : key)} aria-expanded={isOpen} aria-controls={`m-sub-${key}`} aria-label={`${isOpen ? "Collapse" : "Expand"} ${item.label} pages`} className="h-12 w-12 -mr-3 grid place-items-center text-[#FF9000]" data-testid={`m-nav-${key}-toggle`}>
                  <ChevronDown className={`transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
                </button>
              </div>
              <div id={`m-sub-${key}`} className={`${isOpen ? "flex" : "hidden"} pl-3 mb-1 flex-col border-l border-white/10 ml-1`}>
                {item.sections.map(([heading, links], i) => (
                  <div key={heading || i}>
                    {heading && <div className="pl-3 pt-3 pb-1 text-[10px] uppercase tracking-widest text-[#FF9000]/80 font-bold">{heading}</div>}
                    {links.map((d) => (
                      <Link key={d.to} to={d.to} onClick={close} className={`min-h-[44px] flex items-center gap-2 pl-3 text-sm font-semibold uppercase tracking-wider hover:text-[#FF9000] ${d.accent ? "text-[#FF9000]" : "text-white/70"}`} data-testid={`m-${d.testid}`}>{d.label}{d.badge && <Badge>{d.badge}</Badge>}</Link>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </nav>
      <div className="shrink-0 px-6 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] border-t border-white/10 bg-[#111] grid gap-2" data-testid="mobile-menu-cta">
        <a href={COMPANY.phoneHref} data-location="menu" className="border border-white/25 text-white rounded-full px-6 min-h-[48px] flex items-center justify-center gap-2 font-bold uppercase tracking-wider text-sm" data-testid="m-nav-phone"><Phone size={16} className="text-[#FF9000]" /> Call {COMPANY.phone}</a>
        <Link to="/contact" data-cta="free-visit" data-location="menu" onClick={close} className="btn-accent rounded-full px-6 min-h-[48px] flex items-center justify-center gap-2 text-center uppercase tracking-wider" data-testid="m-nav-cta"><CalendarCheck size={16} /> Book a free visit</Link>
      </div>
    </div>
  );
};

// 56px sticky bar on phones (<768px) plus the home-indicator inset; "Call" is a tel: link, "Free visit" opens the contact form.
export const MobileCallBar = () => {
  const { pathname } = useLocation();
  if (!showCallBar(pathname)) return null;
  return (
    <div className="fixed bottom-0 inset-x-0 z-40 grid grid-cols-2 md:hidden border-t border-white/10 pb-[env(safe-area-inset-bottom)] bg-[#111]" data-testid="mobile-callbar">
      <a href={COMPANY.phoneHref} aria-label={`Call ${COMPANY.phone}`} data-location="sticky_bar" className="bg-[#111] text-white h-14 flex items-center justify-center gap-2 font-bold text-sm uppercase tracking-wider" data-testid="callbar-call"><Phone size={16} className="text-[#FF9000]" /> Call</a>
      <Link to="/contact" data-cta="free-visit" data-location="sticky_bar" className="btn-accent h-14 flex items-center justify-center gap-2 text-sm uppercase tracking-wider" data-testid="callbar-quote">Free visit</Link>
    </div>
  );
};

// Footer link columns: always open on desktop; a closed-by-default 48px accordion on phones/tablets with 44px link rows.
// The column title is a real <h2> at every width (the phone toggle button lives inside it) so the footer never breaks heading order.
const FooterColumn = ({ title, children, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={testId}>
      <h2 className="text-white font-bold uppercase text-xs tracking-widest lg:mb-4">
        <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} aria-controls={`footer-${testId}`} className="lg:hidden w-full min-h-[48px] flex items-center justify-between uppercase text-xs tracking-widest border-b border-white/10" data-testid={`${testId}-toggle`}>
          {title}<ChevronDown className={`text-[#FF9000] transition-transform duration-300 ${open ? "rotate-180" : ""}`} size={18} />
        </button>
        <span className="hidden lg:block">{title}</span>
      </h2>
      <ul id={`footer-${testId}`} className={`${open ? "block" : "hidden"} lg:block max-lg:pt-2 max-lg:pb-3 lg:space-y-2 text-sm [&_a]:tap-target`}>
        {children}
      </ul>
    </div>
  );
};

const FL = ({ to, children, testId }) => <li><Link to={to} className="hover:text-[#FF9000]" data-testid={testId}>{children}</Link></li>;
const SOCIAL = "h-11 w-11 lg:h-10 lg:w-10 grid place-items-center border border-white/15 rounded-full hover:border-[#FF9000] hover:text-[#FF9000] transition-colors";
const PILL = "h-11 lg:h-10 px-4 grid place-items-center border border-white/15 rounded-full hover:border-[#FF9000] hover:text-[#FF9000] transition-colors text-xs font-bold";

// BuildZoom badge + its impression pixel: third-party requests that start only when the footer scrolls into view.
const BuildZoomBadge = () => {
  const ref = useRef(null);
  const near = useNearViewport(ref, "200px 0px");
  return (
    <div ref={ref} className="flex flex-col items-start sm:items-center min-h-[56px]" data-testid="footer-buildzoom" data-loaded={near ? "true" : "false"}>
      {near && (<>
        <a target="_blank" rel="noopener noreferrer" title="BuildZoom.com" href="https://www.buildzoom.com/contractor/id-builder-construction-federal-way-wa?ad_location=co_website" data-testid="footer-buildzoom-badge" className="tap-target">
          <img alt="BuildZoom.com" width="140" height="56" loading="lazy" decoding="async" className="block w-[140px] h-auto opacity-80 hover:opacity-100 transition-opacity" src="https://badges.buildzoom.com/2023/fon2.png" />
        </a>
        <img src="https://track.buildzoom.com//badge_load?track_id=KDmvB2&entity=Contractor&event_type=impression&ad_type=contractor_badge" width="1" height="1" alt="" className="absolute h-px w-px opacity-0 pointer-events-none" aria-hidden="true" />
      </>)}
    </div>
  );
};

export const Footer = () => {
  const { pathname } = useLocation();
  return (
    <footer className={`bg-[#0c0c0c] text-white/70 pt-16 lg:pt-20 pb-10 ${showCallBar(pathname) ? "max-md:pb-[calc(3.5rem+env(safe-area-inset-bottom)+1.5rem)]" : ""}`} data-testid="site-footer">
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10 grid gap-6 lg:gap-x-8 lg:gap-y-12 lg:grid-cols-3 xl:grid-cols-[1.25fr_repeat(5,1fr)]">
        <div>
          <img src={asset("/logo.webp")} alt={LOGO_ALT} className="h-20 w-auto mb-3" width="168" height="80" />
          <div className="text-xs lg:text-[10px] tracking-[0.35em] text-[#FF9000] font-bold mb-5">BUILDING BLESSINGS</div>
          <p className="text-sm leading-relaxed max-w-xs">An in-house, all-trades builder serving the Puget Sound region and beyond. Design, permits, and construction under one roof.</p>
          <div className="flex flex-wrap gap-3 mt-5">
            <a href={COMPANY.instagram} target="_blank" rel="noopener noreferrer" className={SOCIAL} aria-label="Instagram" data-testid="footer-instagram"><Instagram size={17} /></a>
            <a href={COMPANY.linkedin} target="_blank" rel="noopener noreferrer" className={SOCIAL} aria-label="LinkedIn" data-testid="footer-linkedin"><Linkedin size={17} /></a>
            <a href={COMPANY.facebook} target="_blank" rel="noopener noreferrer" className={SOCIAL} aria-label="Facebook" data-testid="footer-facebook"><Facebook size={17} /></a>
            <a href={COMPANY.yelp} target="_blank" rel="noopener noreferrer" className={PILL} data-testid="footer-yelp">Yelp</a>
            <a href={COMPANY.houzz} target="_blank" rel="noopener noreferrer" className={PILL} data-testid="footer-houzz">Houzz</a>
          </div>
        </div>
        <FooterColumn title="Services" testId="footer-services">
          {NAV_SERVICES.map((s) => <FL key={s.slug} to={`/services/${s.slug}`}>{serviceName(s)}</FL>)}
          <li><Link to="/services" className="text-[#FF9000] font-semibold">All services →</Link></li>
        </FooterColumn>
        <FooterColumn title="Pricing" testId="footer-pricing">
          <FL to="/estimate" testId="footer-estimate">Estimate calculator</FL>
          <FL to="/cost" testId="footer-cost-guides">Cost guides</FL>
          <FL to="/financing">Financing</FL>
          <li><LenderLink lender="acorn" placement="footer" className="hover:text-[#FF9000]" testId="footer-prequalify">Pre-qualify with Acorn</LenderLink></li>
          <li><LenderLink lender="hfs" placement="footer" className="hover:text-[#FF9000]" testId="footer-prequalify-hfs">Check rates with HFS</LenderLink></li>
          <li><Link to="/design" className="hover:text-[#FF9000] inline-flex items-center gap-1.5" data-testid="footer-design">3D Design Studio <span className="text-xs lg:text-[9px] leading-none font-bold uppercase tracking-wider text-[#FF9000] border border-[#FF9000]/50 rounded-full px-1.5 py-0.5" data-testid="footer-design-beta">Beta</span></Link></li>
          <FL to="/my-designs" testId="footer-my-designs">My saved designs</FL>
        </FooterColumn>
        <FooterColumn title="Company" testId="footer-company">
          <FL to="/about">About</FL>
          <FL to="/process">Our Process</FL>
          <FL to="/about#what-makes-us-different">What Makes Us Different</FL>
          <FL to="/reviews">Reviews</FL>
          <FL to="/blog">Resource Center</FL>
          <FL to="/faq" testId="footer-faq">FAQ</FL>
          <FL to="/careers">Careers</FL>
          <FL to="/trade-partners">Trade Partners</FL>
          <FL to="/contact" testId="footer-contact">Contact</FL>
        </FooterColumn>
        <FooterColumn title="Locations" testId="footer-locations">
          {CITIES.filter((c) => !c.servedFrom).map((c) => <FL key={c.slug} to={`/service-areas/${c.slug}`} testId={`footer-city-${c.slug}`}>{c.name}, {c.state}{c.hq ? " (HQ)" : ""}</FL>)}
          <li className="pt-2 text-[10px] uppercase tracking-widest text-[#FF9000]/80 font-bold" data-testid="footer-also-serving">{ALSO_SERVING}</li>
          {CITIES.filter((c) => c.servedFrom).map((c) => <FL key={c.slug} to={`/service-areas/${c.slug}`} testId={`footer-city-${c.slug}`}>{c.label || c.name}, {c.state}</FL>)}
          <li><Link to="/service-areas" className="text-[#FF9000] font-semibold" data-testid="footer-all-locations">All locations →</Link></li>
        </FooterColumn>
        <div>
          <FooterColumn title="Our companies" testId="footer-companies">
            {COMPANIES.map((c) => (
              <li key={c.name} className="max-lg:py-1">
                {c.to
                  ? <Link to={c.to} className="hover:text-[#FF9000] text-white/90 font-semibold" data-testid={`footer-company-${c.id}`}>{c.name}</Link>
                  : <a href={c.url} target="_blank" rel="noopener noreferrer" className="hover:text-[#FF9000] text-white/90 font-semibold" data-testid={`footer-company-${c.id}`}>{c.name}</a>}
                {c.line && <span className="block text-xs text-white/50 leading-snug">{c.line}</span>}
              </li>
            ))}
          </FooterColumn>
          <div className="mt-8" data-testid="footer-contact-block">
            <h2 className="text-white font-bold uppercase text-xs tracking-widest mb-4">Contact</h2>
            <ul className="space-y-2 text-sm">
              <li className="text-white/90 font-semibold">ID Builder Construction</li>
              <li>36058 21st Ln S, Federal Way, WA 98003</li>
              <li><a href={COMPANY.phoneHref} data-location="footer" className="hover:text-[#FF9000] font-bold text-white tap-target">{COMPANY.phone}</a></li>
              <li><a href={`mailto:${COMPANY.email}`} data-location="footer" className="hover:text-[#FF9000] tap-target break-all">{COMPANY.email}</a></li>
              <li>Mon–Fri 8 am–6 pm, Sat 9 am–4 pm</li>
            </ul>
            <div className="mt-5 text-[13px] lg:text-xs leading-relaxed border-t border-white/10 pt-4" data-testid="footer-licenses">
              WA Reg. {COMPANY.waReg} · AZ ROC {COMPANY.azRoc} · ID Reg. {COMPANY.idReg} · MT Reg. {COMPANY.mtLic}<br />
              Licensed · Bonded · Insured
            </div>
          </div>
        </div>
      </div>
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10 mt-10 lg:mt-14 pt-6 border-t border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-xs">
        <span>© {new Date().getFullYear()} ID-Builder Construction LLC. All rights reserved.</span>
        <BuildZoomBadge />
        <div className="flex gap-5">
          <Link to="/privacy" className="hover:text-[#FF9000] tap-target" data-testid="footer-privacy">Privacy</Link>
          <Link to="/terms" className="hover:text-[#FF9000] tap-target" data-testid="footer-terms">Terms</Link>
          <Link to="/accessibility" className="hover:text-[#FF9000] tap-target" data-testid="footer-accessibility">Accessibility</Link>
        </div>
      </div>
    </footer>
  );
};
